import lodashGet from 'lodash/get';
import { useUser } from './user';
import { defineStore } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import { signalrListen } from '@lib/signalr';
import { queueSnapshotWrite } from '@lib/snapshot-cache';
import { snapshotIdentity } from '@/store/warm-boot';
import dtoHashes from '@/lib/dto-hashes.json';

/**
 * Where a configuration value is written: the user's own cell or the brand's. Reading needs no
 * scope — see `set`.
 */
export type ConfigScope = 'user' | 'brand';

export const useUserConfig = defineStore('user-config', () => {
  const api = useUser().$api; //Can't use normal useApi() here, because this store is used outside of the Vue tree

  const loaded = ref<null | any>(null);

  const settings = computed(() => loaded.value?.settings);
  const permissions = computed(() => loaded.value?.permissions);

  /**
   * What the brand calls the date a timeline counts back from. Most say "drop date"; some say
   * "shop floor", "in store" or "launch", and a critical path is read against whichever one the
   * brand actually plans to.
   */
  const timelineAnchorLabel = computed<string>(() => loaded.value?.settings?.timeline?.anchorLabel ?? 'Drop date');

  async function ensureLoaded() {
    if (!loaded.value) {
      return await load();
    }

    return loaded.value;
  }

  async function load() {
    const brandId = useUser().brandId;
    //@ts-ignore No proper TS-types for the generated api-client yet.
    const { userConfig } = await api.userConfiguration_GetConfiguration(brandId);
    loaded.value = userConfig;
    persistSnapshot();

    return loaded.value;
  }

  // Seed from a persisted snapshot for warm boot. No-op when a fetch already landed —
  // never overwrite fresh data with a snapshot.
  function seed(config: unknown) {
    if (!loaded.value) loaded.value = config;
  }

  function persistSnapshot() {
    const identity = snapshotIdentity();
    if (!identity || !loaded.value) return;
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    queueSnapshotWrite(
      identity.userId,
      identity.brandId,
      'userConfiguration',
      toRaw(loaded.value),
      dtoHashes.UserConfigSection
    );
  }

  function get(key: string) {
    return lodashGet(loaded.value, key);
  }

  // Uses same -- syntax as `set` does.
  function getWithDashes(key: string) {
    return get(key.replace(/--/g, '.'));
  }

  /**
   * Personal writes are collected and sent together, because they arrive together: applying a
   * saved table view writes the columns and the active view in the same tick, and each write
   * costs a request, a save, and a SignalR notification that reloads this store. One request
   * per burst instead of one per key. Last value wins, as it would if they were sent in order.
   */
  const pendingPersonal = new Map<string, any>();
  let pendingFlush: Promise<void> | null = null;

  /**
   * One burst in flight at a time, personal or brand-wide. Each answer is the whole configuration
   * as the server has it, so two overlapping writes whose answers arrive out of order would put
   * the earlier one back — a setting the user just changed reverting a moment after they changed
   * it.
   *
   * A failed write does not hold up the ones behind it: its own caller is told, and the next
   * burst carries the current state anyway.
   */
  let writes: Promise<void> = Promise.resolve();

  function flushPersonal() {
    const brandId = useUser().brandId;
    const values = [...pendingPersonal].map(([key, value]) => ({ key, value: value ?? undefined }));

    pendingPersonal.clear();
    pendingFlush = null;

    const send = async () => {
      const result: any = await api.userConfiguration_SetPersonalConfigurationBulk(brandId, { values });

      loaded.value = result.userConfig;
      persistSnapshot();
    };

    writes = writes.then(send, send);

    return writes;
  }

  function schedulePersonalFlush() {
    // A macrotask rather than a microtask: the writes that belong together are made by a watcher
    // and its caller, which land in separate microtasks of the same tick.
    pendingFlush ??= new Promise<void>((resolve, reject) => {
      setTimeout(() => flushPersonal().then(resolve, reject), 0);
    });

    return pendingFlush;
  }

  /**
   * Brand-wide writes are collected per tick like personal ones, but kept as a list of updates
   * rather than one value per key. A key can hold a whole document — the onboarding checklist
   * is one — and several edits to it in a burst each carry their own change. Keeping only the
   * last would silently drop the rest.
   *
   * An update that is a function runs when its burst is sent, against the value as the previous
   * burst left it, so each edit builds on the ones before it instead of on the snapshot its
   * caller read before those had been saved. The value it is given is the resolved one: a
   * user's own value for the key would stand in for the brand's.
   *
   * A burst is one request per key it touches, not one bulk request: the single-key endpoints
   * answer with the resolved configuration this store holds, and brand-wide bursts rarely span
   * more than one key.
   */
  type BrandUpdate = { key: string; update: (current: any) => any };
  let pendingBrand: BrandUpdate[] = [];
  let pendingBrandFlush: Promise<void> | null = null;

  function flushBrand() {
    const brandId = useUser().brandId;
    const updates = pendingBrand;

    pendingBrand = [];
    pendingBrandFlush = null;

    const send = async () => {
      const next = new Map<string, any>();
      for (const { key, update } of updates) {
        next.set(key, update(next.has(key) ? next.get(key) : getWithDashes(key)));
      }

      for (const [key, value] of next) {
        const result: any =
          value === null || value === undefined
            ? await api.userConfiguration_UnsetGlobalConfiguration(brandId, key)
            : await api.userConfiguration_SetGlobalConfiguration(brandId, { key, value });

        loaded.value = result.userConfig;
        persistSnapshot();
      }
    };

    writes = writes.then(send, send);

    return writes;
  }

  function scheduleBrandFlush() {
    pendingBrandFlush ??= new Promise<void>((resolve, reject) => {
      setTimeout(() => flushBrand().then(resolve, reject), 0);
    });

    return pendingBrandFlush;
  }

  /**
   * Writes one key at one of the three scopes.
   *
   * Which scope a value came from is the server's business — it hands back one resolved
   * configuration, a user's own value standing over the brand's — so only the write names a scope.
   *
   * A brand-wide write may pass a function of the current value instead of a value; see
   * `pendingBrand`. Anything that edits part of a value it read should, or concurrent edits undo
   * each other.
   */
  async function set(key: string, value: any, scope: ConfigScope = 'user') {
    if (scope === 'user') {
      pendingPersonal.set(key, value ?? null);
      return await schedulePersonalFlush();
    }

    pendingBrand.push({ key, update: typeof value === 'function' ? value : () => value });
    return await scheduleBrandFlush();
  }

  function listen() {
    signalrListen('UserConfigurationUpdate', load, { listenForever: true });
  }

  return {
    loaded,
    settings,
    permissions,
    timelineAnchorLabel,

    ensureLoaded,
    load,
    seed,
    get,
    getWithDashes,
    set,
    listen,
  };
});
